import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, MapPin, CheckCircle2, Code2, FolderGit2,
  Sparkles, Send, Loader2, AlertCircle, Layers, BookOpen,
  Cpu, ShieldCheck,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Eyebrow } from "../components/Eyebrow";
import { Button } from "../components/ui/button";
import { PARTNERS, COMPANY_ADDRESS } from "../mock";
import { LogoImage } from "../components/LogoImage";
import { PRODUCTS_DATA } from "../data/productsData";
import { submitLead, emptyLeadForm } from "../lib/api";

const ACCENTS = [
  "from-[#15348C] to-[#0A1F5C]",
  "from-[#6BBE32] to-[#4F9622]",
  "from-[#8BD835] to-[#6BBE32]",
  "from-[#4F9622] to-[#15348C]",
];

const HIGHLIGHT_ICONS = [Code2, FolderGit2, Sparkles, CheckCircle2, ShieldCheck];
const EXPLORE_ICONS = [Code2, Sparkles, Layers, Cpu, BookOpen];

const CODE_LINES = [
  { w: "w-1/3", c: "bg-[#8BD835]" },
  { w: "w-2/3", c: "bg-white/70" },
  { w: "w-1/2", c: "bg-[#6BBE32]" },
  { w: "w-3/4", c: "bg-white/50" },
  { w: "w-2/5", c: "bg-[#8BD835]" },
  { w: "w-3/5", c: "bg-white/70" },
  { w: "w-1/4", c: "bg-[#6BBE32]" },
];

const INTEREST_OPTIONS = [
  "AI-Powered Coding Platform",
  "AI-Powered Assessments",
  "Enterprise Learning",
  "Hands-On Labs",
  "Corporate Training",
  "Other",
];

const AIPoweredCodingPlatformPage = () => {
  const data = PRODUCTS_DATA["ai-powered-coding-platform"];
  const {
    eyebrow, title, heroSubheadline, subtitle, highlights,
    whyEyebrow, whyHeading, whyCopy, whyCopy2, whyCtaLabel, whyCtaHref,
    capabilitiesHeading, capabilities,
    outcomesEyebrow, outcomesHeading, outcomesIntro, outcomes,
    calloutHeadingA, calloutHeadingB, calloutCopy, calloutStats,
    whoEyebrow, whoHeading, whoIntro, whoItems,
    exploreEyebrow, exploreHeading, exploreIntro, exploreCards,
    ctaHeadline, ctaSupporting,
  } = data;

  const [activeUseCase, setActiveUseCase] = useState(0);
  const [form, setForm] = useState({ ...emptyLeadForm(), interest: "AI-Powered Coding Platform" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await submitLead({ ...form, source: "ai-powered-coding-platform-page", formType: "product-enquiry" });
      setStatus({ state: "success", message: "Thanks! Our enterprise team will reach out within one working day." });
      setForm({ ...emptyLeadForm(), interest: "AI-Powered Coding Platform" });
      setTimeout(() => setStatus({ state: "idle", message: "" }), 6000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", message: typeof detail === "string" ? detail : "Something went wrong. Please try again." });
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 1) HERO — split layout */}
      <section className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
        <div
          className="sg-grid-drift absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "22px 22px" }}
        />
        <div className="sg-float-a pointer-events-none absolute -top-24 right-0 w-96 h-96 bg-[#6BBE32]/15 rounded-full blur-3xl" />
        <div className="sg-float-b pointer-events-none absolute -bottom-24 -left-10 w-80 h-80 bg-[#8BD835]/15 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <div className="flex"><Eyebrow dark>{eyebrow}</Eyebrow></div>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
              AI-Powered <span className="text-[#8BD835]">Coding Platform</span>
            </h1>
            {heroSubheadline && <p className="mt-3 text-xl font-semibold text-gray-100">{heroSubheadline}</p>}
            <p className="mt-4 text-gray-300 leading-relaxed max-w-xl">{subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="sg-btn-glow bg-[#6BBE32] hover:bg-[#4F9622] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#6BBE32]/30">
                  Talk to Our Experts <ArrowRight size={17} className="ml-1" />
                </Button>
              </Link>
              <Link to="/offerings/all">
                <Button className="bg-transparent border-2 border-white/40 hover:bg-white/10 hover:border-white text-white font-semibold px-6 h-12 rounded-md">
                  Explore Skillsgain
                </Button>
              </Link>
            </div>
            {highlights && highlights.length > 0 && (
              <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3">
                {highlights.map((h, i) => {
                  const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
                  return (
                    <div key={h} className="flex items-center gap-2 text-sm text-gray-300">
                      <Icon size={16} className="text-[#8BD835]" /> {h}
                    </div>
                  );
                })}
              </div>
            )}
          </Reveal>

          {/* Illustrative code-editor mockup — icons/CSS only, no photography */}
          <Reveal variant="right" delay={120}>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-4 bg-gradient-to-br from-[#6BBE32]/20 to-[#8BD835]/10 rounded-3xl blur-2xl" />
              <div className="relative bg-[#0E1F4A] rounded-2xl shadow-2xl overflow-hidden border border-white/10">
                <div className="flex items-center gap-1.5 px-4 py-3 bg-black/20 border-b border-white/10">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                  <span className="ml-3 text-xs text-gray-400 font-mono">solution.py</span>
                </div>
                <div className="p-6 font-mono space-y-2.5">
                  {CODE_LINES.map((l, i) => (
                    <div key={i} className="sg-chip flex items-center gap-3" style={{ animationDelay: `${i * 90}ms` }}>
                      <span className="text-xs text-gray-500 w-4 text-right">{i + 1}</span>
                      <span className={`h-2.5 rounded ${l.w} ${l.c}`} />
                    </div>
                  ))}
                </div>
                <div className="px-6 pb-5 flex items-center gap-2 text-xs text-[#8BD835] font-mono">
                  <CheckCircle2 size={14} /> All test cases passed
                </div>
              </div>
              <div className="sg-chip absolute -top-5 -right-5 bg-white rounded-xl shadow-xl px-4 py-2 text-xs font-bold text-[#15348C] border border-gray-100" style={{ animationDelay: "480ms" }}>
                Real Skills for Real Opportunities
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2) WHY SKILLSGAIN + CORE CAPABILITIES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12">
          <Reveal variant="left">
            <Eyebrow color="#6BBE32">{whyEyebrow}</Eyebrow>
            <h2 className="text-[#15348C] leading-tight">{whyHeading}</h2>
            <p className="mt-4 text-gray-600 leading-relaxed">{whyCopy}</p>
            <p className="mt-3 text-gray-600 leading-relaxed">{whyCopy2}</p>
            {whyCtaLabel && (
              <Link to={whyCtaHref || "/contact"} className="mt-5 inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                {whyCtaLabel} <ArrowRight size={16} />
              </Link>
            )}
          </Reveal>

          <Reveal variant="right" delay={100}>
            <div className="mb-4 text-xs font-bold tracking-widest text-[#4F9622]">{capabilitiesHeading?.toUpperCase()}</div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
              {capabilities.map((c, i) => {
                const accent = ["#15348C", "#6BBE32", "#4F9622", "#8BD835", "#0A1F5C", "#6BBE32"][i % 6];
                return (
                  <div key={c.title} className="flex gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0"
                      style={{ backgroundColor: `${accent}1A`, color: accent }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <div className="font-semibold text-[#15348C] text-sm">{c.title}</div>
                      <p className="text-sm text-gray-600 mt-0.5">{c.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3) BUSINESS OUTCOMES + CALLOUT CARD */}
      <section className="py-20 bg-gradient-to-br from-[#F8F9FA] to-[#EFF3E8]">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal variant="left">
            <Eyebrow color="#4F9622">{outcomesEyebrow}</Eyebrow>
            <h2 className="text-[#15348C] leading-tight">{outcomesHeading}</h2>
            {outcomesIntro && <p className="mt-3 text-gray-600 leading-relaxed">{outcomesIntro}</p>}
            <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {outcomes.map((o) => (
                <div key={o} className="flex items-start gap-2.5">
                  <CheckCircle2 size={18} className="text-[#6BBE32] shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{o}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal variant="right" delay={100}>
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#15348C] to-[#0A1F5C] p-8 text-white">
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.08]"
                style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "18px 18px" }}
              />
              <div className="sg-float-c pointer-events-none absolute -bottom-10 -right-10 w-52 h-52 bg-[#6BBE32]/20 rounded-full blur-3xl" />
              <div className="relative">
                <h3 className="text-2xl lg:text-3xl font-bold leading-tight">
                  {calloutHeadingA} <span className="text-[#8BD835]">{calloutHeadingB}</span>
                </h3>
                <p className="mt-3 text-gray-300 text-sm leading-relaxed">{calloutCopy}</p>
                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-5">
                  {calloutStats.map((s) => (
                    <span key={s} className="text-sm font-semibold text-white">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4) USE CASES — interactive tabs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-2xl mb-2">
              <Eyebrow color="#6BBE32">{whoEyebrow}</Eyebrow>
              <h2 className="text-[#15348C] leading-tight">{whoHeading}</h2>
              {whoIntro && <p className="mt-2 text-gray-600">{whoIntro}</p>}
            </div>
          </Reveal>
          <Reveal delay={60}>
            <div className="flex flex-wrap gap-2 border-b border-gray-100 mt-8 mb-8">
              {whoItems.map((w, i) => (
                <button
                  key={w.title}
                  onClick={() => setActiveUseCase(i)}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    activeUseCase === i ? "text-[#15348C] border-[#6BBE32]" : "text-gray-400 border-transparent hover:text-[#15348C]"
                  }`}
                >
                  {w.title}
                </button>
              ))}
            </div>
          </Reveal>
          <Reveal key={whoItems[activeUseCase].title} delay={0}>
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 flex flex-wrap items-center justify-between gap-4">
              <p className="text-lg text-gray-700 max-w-2xl">{whoItems[activeUseCase].desc}</p>
              <Link to="/contact" className="inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors shrink-0">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5) TRUSTED BY */}
      <section className="py-14 bg-[#F8F9FA] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div
              className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-80"
              style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}
            >
              {PARTNERS.slice(0, 10).map((p) => (
                <LogoImage key={p.name} src={p.logo} alt={p.name}
                  className="h-8 max-w-[120px] object-contain"
                  fallbackClassName="text-[#15348C] font-bold text-sm" />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6) EXPLORE SKILLSGAIN */}
      {exploreCards && exploreCards.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div className="max-w-2xl">
                  <Eyebrow color="#4F9622">{exploreEyebrow}</Eyebrow>
                  <h2 className="text-[#15348C] leading-tight">{exploreHeading}</h2>
                  {exploreIntro && <p className="mt-2 text-gray-600">{exploreIntro}</p>}
                </div>
                <Link to="/offerings/all" className="hidden sm:inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                  View All Products <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {exploreCards.map((c, i) => {
                const Icon = EXPLORE_ICONS[i % EXPLORE_ICONS.length];
                const accent = ACCENTS[i % ACCENTS.length];
                return (
                  <Reveal key={c.title} variant="up" delay={i * 90}>
                    <Link
                      to={c.href}
                      className={`group relative flex flex-col rounded-2xl overflow-hidden h-full border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                        c.current ? "border-[#6BBE32] bg-[#6BBE32]/5" : "border-gray-100 bg-white"
                      }`}
                    >
                      <div className={`h-16 flex items-center justify-center bg-gradient-to-br ${accent}`}>
                        <Icon size={22} className="text-white" />
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors" style={{ fontSize: "0.95rem" }}>{c.title}</h3>
                        <p className="mt-1 text-gray-600 text-xs leading-relaxed flex-1">{c.desc}</p>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">
                          Learn More <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 7) FINAL CTA + LEAD FORM */}
      <section className="py-20 bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "20px 20px" }}
        />
        <div className="sg-float-a pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-3xl" />
        <div className="sg-float-c pointer-events-none absolute -bottom-24 -left-24 w-80 h-80 bg-[#8BD835]/20 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10">
          <Reveal variant="left">
            <Eyebrow dark>TALK TO OUR EXPERTS</Eyebrow>
            <h2 className="text-white leading-tight">{ctaHeadline}</h2>
            {ctaSupporting && <p className="mt-4 text-gray-300 leading-relaxed max-w-md">{ctaSupporting}</p>}
            <div className="mt-8 space-y-3">
              <a href="mailto:info@skillsgain.com" className="flex items-center gap-2 text-gray-200 hover:text-[#8BD835] transition-colors">
                <Mail size={16} /> info@skillsgain.com
              </a>
              <div className="flex items-start gap-2 text-gray-300">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>{COMPANY_ADDRESS.line1}, {COMPANY_ADDRESS.line2}<br />{COMPANY_ADDRESS.line3}, {COMPANY_ADDRESS.line4}</span>
              </div>
            </div>
          </Reveal>

          <Reveal variant="right" delay={100}>
            <div className="bg-white rounded-2xl p-6 lg:p-7 text-left">
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="coding-lead-form">
                <LeadField label="Full Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <LeadField label="Work Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <LeadField label="Company" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
                <LeadField label="Phone" type="tel" value={form.mobile_no} onChange={(v) => setForm({ ...form, mobile_no: v })} />
                <div className="sm:col-span-2">
                  <label className="text-sm font-semibold text-[#15348C]">Requirement / Interest</label>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors text-sm"
                  >
                    {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-semibold text-[#15348C]">Message<span className="text-[#4F9622]">*</span></label>
                  <textarea
                    required rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Tell us about your requirements…"
                    className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors text-sm"
                  />
                </div>
                <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
                  <Button type="submit" disabled={status.state === "loading"}
                    className="sg-btn-glow bg-[#15348C] hover:bg-[#0A1F5C] text-white px-6 h-11 rounded-md disabled:opacity-60">
                    {status.state === "loading"
                      ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>)
                      : (<><Send size={16} className="mr-2" /> Talk to Our Experts</>)}
                  </Button>
                  {status.state === "success" && (
                    <span className="text-[#4F9622] text-sm flex items-center gap-1"><CheckCircle2 size={16} /> {status.message}</span>
                  )}
                  {status.state === "error" && (
                    <span className="text-red-600 text-sm flex items-center gap-1"><AlertCircle size={16} /> {status.message}</span>
                  )}
                </div>
                <p className="sm:col-span-2 text-xs text-gray-400">
                  By submitting this form, you agree to be contacted by Skillsgain regarding enterprise learning, assessment, and technology services.
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const LeadField = ({ label, value, onChange, type = "text", required }) => (
  <div>
    <label className="text-sm font-semibold text-[#15348C]">{label}{required && <span className="text-[#4F9622]">*</span>}</label>
    <input
      type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors text-sm"
    />
  </div>
);

export default AIPoweredCodingPlatformPage;
